export interface IPromotionDescriptionFlags {
  isTop: boolean;
  isPremium: boolean;
  isStandard: boolean;
  isAuction: boolean;
  auctionBidRubles: number | null;
}

interface IPromotionLabelParsingResult {
  promotionLabel: string;
  isAuction: boolean;
  auctionBidRubles: number | null;
}

const NON_BREAKING_SPACE_REGEXP = /\u00a0/g;
const SPACES_REGEXP = /\s+/g;
const AUCTION_PART_REGEXP = /^(.+?)\s*\+\s*([1-9]\d{0,2}(?: \d{3})+|[1-9]\d*)\s*(?:₽)?$/;

const PROMOTION_LABEL_FLAGS = {
  Топ: 'isTop',
  Премиум: 'isPremium',
  Стандарт: 'isStandard',
} as const;

type TPromotionLabel = keyof typeof PROMOTION_LABEL_FLAGS;

const getEmptyPromotionDescriptionFlags = (): IPromotionDescriptionFlags => ({
  isTop: false,
  isPremium: false,
  isStandard: false,
  isAuction: false,
  auctionBidRubles: null,
});

const normalizePromotionDescription = (label: string): string =>
  label.replace(NON_BREAKING_SPACE_REGEXP, ' ').trim().replace(SPACES_REGEXP, ' ');

const isPromotionLabel = (label: string): label is TPromotionLabel =>
  Object.prototype.hasOwnProperty.call(PROMOTION_LABEL_FLAGS, label);

const parseAuctionBidRubles = (value: string): number => Number(value.replace(SPACES_REGEXP, ''));

const getPromotionLabelParsingResult = (label: string): IPromotionLabelParsingResult => {
  const auctionMatch = label.match(AUCTION_PART_REGEXP);

  if (!auctionMatch) {
    return {
      promotionLabel: label,
      isAuction: false,
      auctionBidRubles: null,
    };
  }

  return {
    promotionLabel: auctionMatch[1],
    isAuction: true,
    auctionBidRubles: parseAuctionBidRubles(auctionMatch[2]),
  };
};

export const parsePromotionDescription = (label: string | null | undefined): IPromotionDescriptionFlags => {
  if (!label) {
    return getEmptyPromotionDescriptionFlags();
  }

  const normalizedLabel = normalizePromotionDescription(label);

  if (!normalizedLabel) {
    return getEmptyPromotionDescriptionFlags();
  }

  const { promotionLabel, isAuction, auctionBidRubles } = getPromotionLabelParsingResult(normalizedLabel);

  if (!isPromotionLabel(promotionLabel)) {
    return getEmptyPromotionDescriptionFlags();
  }

  const promotionFlag = PROMOTION_LABEL_FLAGS[promotionLabel];
  const flags = getEmptyPromotionDescriptionFlags();

  flags[promotionFlag] = true;
  flags.isAuction = isAuction;
  flags.auctionBidRubles = auctionBidRubles;

  return flags;
};
